'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useState, useEffect } from 'react'
import { cn } from '@/lib/utils'

const links = [
  { href: '/services', label: 'Services' },
  { href: '/about', label: 'About' },
  { href: '/insights', label: 'Insights' },
  { href: '/faq', label: 'FAQ' },
]

export default function Nav() {
  const pathname = usePathname()
  const [open, setOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', handler)
    return () => window.removeEventListener('scroll', handler)
  }, [])

  useEffect(() => {
    setOpen(false)
  }, [pathname])

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? 'glass-nav' : ''}`}
      style={{
        backgroundColor: scrolled ? undefined : 'transparent',
        borderBottom: scrolled ? undefined : '1px solid transparent',
      }}
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-8 h-[72px] flex items-center justify-between">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-2.5 group">
          <svg
            width="28"
            height="28"
            viewBox="0 0 28 28"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden
          >
            <circle cx="14" cy="14" r="13" fill="#818CF8" fillOpacity="0.12" stroke="#818CF8" strokeWidth="1.5"/>
            <path d="M9 14L12 17L19 10" stroke="#818CF8" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span
            className="text-lg tracking-tight"
            style={{ fontFamily: 'var(--font-syne)', fontWeight: 700, color: '#F4F4F5' }}
          >
            Nexus
          </span>
        </Link>

        {/* Desktop nav — all items right-aligned in one pill */}
        <nav
          className="hidden md:flex items-center gap-1 px-2 py-1 rounded-xl"
          style={{
            border: '1px solid rgba(255,255,255,0.08)',
            background: 'rgba(255,255,255,0.04)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
          }}
        >
          {links.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className={cn(
                'text-sm px-4 py-2 rounded-lg transition-all duration-200',
                pathname === link.href
                  ? 'text-[#F4F4F5] font-medium bg-white/[0.08]'
                  : 'text-[#A1A1AA] hover:text-[#F4F4F5] hover:bg-white/[0.06]'
              )}
              style={{ fontFamily: 'var(--font-jakarta)' }}
            >
              {link.label}
            </Link>
          ))}
          {/* Divider */}
          <span
            aria-hidden
            style={{ display: 'block', width: '1px', height: '16px', background: 'rgba(255,255,255,0.1)', margin: '0 4px', flexShrink: 0 }}
          />
          <Link
            href="/contact"
            className="text-sm px-4 py-2 rounded-lg transition-all duration-200 tracking-wide bg-[#F4F4F5] text-[#09090B] hover:bg-[#E4E4E7]"
            style={{ fontFamily: 'var(--font-jakarta)', fontWeight: 600 }}
          >
            Get in touch
          </Link>
        </nav>

        {/* Mobile menu button */}
        <button
          className="md:hidden flex flex-col gap-1.5 p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          <span
            className="block h-px w-5 transition-all duration-200 origin-center"
            style={{
              backgroundColor: '#F4F4F5',
              transform: open ? 'rotate(45deg) translate(2px, 2px)' : 'none',
            }}
          />
          <span
            className="block h-px w-5 transition-all duration-200"
            style={{
              backgroundColor: '#F4F4F5',
              opacity: open ? 0 : 1,
            }}
          />
          <span
            className="block h-px w-5 transition-all duration-200 origin-center"
            style={{
              backgroundColor: '#F4F4F5',
              transform: open ? 'rotate(-45deg) translate(2px, -2px)' : 'none',
            }}
          />
        </button>
      </div>

      {/* Mobile menu */}
      <div
        className="md:hidden overflow-hidden transition-all duration-300"
        style={{
          maxHeight: open ? '300px' : '0',
          borderBottom: open ? '1px solid rgba(255,255,255,0.06)' : 'none',
          background: 'rgba(9, 9, 11, 0.92)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
        }}
      >
        <nav className="flex flex-col px-6 pb-6 pt-2 gap-4">
          {links.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className="text-base py-1 transition-colors"
              style={{
                color: pathname === link.href ? '#818CF8' : '#A1A1AA',
                fontFamily: 'var(--font-jakarta)',
              }}
            >
              {link.label}
            </Link>
          ))}
          <Link
            href="/contact"
            className="mt-2 text-sm px-5 py-2.5 rounded-lg text-center"
            style={{
              fontFamily: 'var(--font-jakarta)',
              border: '1px solid rgba(129,140,248,0.4)',
              color: '#818CF8',
              background: 'rgba(129,140,248,0.08)',
            }}
          >
            Get in touch
          </Link>
        </nav>
      </div>
    </header>
  )
}
